import React, { useState, useEffect, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { MapPin, Star, Image as ImageIcon, Globe, Phone, Clock, FileText, RefreshCw, Loader2, ExternalLink, Link2, Lightbulb, TrendingUp, LineChart as LineChartIcon } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";

const PRIO = { high: "#ef4444", medium: "#b45309", low: "#64748b" };
const photoUrl = (ref, w = 300) => ref ? `${process.env.REACT_APP_BACKEND_URL}/api/gbp/photo?ref=${encodeURIComponent(ref)}&w=${w}` : null;

function Ring({ score, color }) {
  return (
    <div className="relative w-24 h-24 rounded-full grid place-items-center shrink-0" style={{ background: `conic-gradient(${color} ${score * 3.6}deg, var(--secondary) 0deg)` }}>
      <div className="absolute inset-2 rounded-full bg-card grid place-items-center">
        <div className="text-center"><div className="text-2xl font-bold font-data" style={{ color }}>{Math.round(score)}</div><div className="text-[9px] uppercase text-muted-foreground">/100</div></div>
      </div>
    </div>
  );
}

export default function GoogleVisibility({ propId, lang }) {
  const L = (it, en) => (lang === "en" ? en : it);
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [linkVal, setLinkVal] = useState("");
  const [showLink, setShowLink] = useState(false);

  const load = useCallback(async (refresh = false) => {
    setLoading(!refresh); if (refresh) setBusy(true);
    try { const { data } = await api.get(`/properties/${propId}/gbp?lang=${lang}${refresh ? "&refresh=true" : ""}`); setData(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); }
    finally { setLoading(false); setBusy(false); }
    try { const { data: h } = await api.get(`/properties/${propId}/gbp/history`); setHistory(h.history || []); } catch { /* ignore */ }
  }, [propId, lang]);
  useEffect(() => { load(); }, [load]);

  const saveLink = async () => {
    if (!linkVal.trim()) return;
    setBusy(true);
    try { await api.post(`/properties/${propId}/gbp/link`, { query: linkVal.trim() }); toast.success(L("Profilo collegato", "Profile linked")); setShowLink(false); setLinkVal(""); await load(true); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || L("Profilo non trovato", "Profile not found")); }
    finally { setBusy(false); }
  };
  const fixCompetitor = async (cid) => {
    const q = window.prompt(L("Incolla il link Google o il nome corretto del competitor:", "Paste the Google link or the competitor's correct name:"));
    if (!q) return;
    setBusy(true);
    try { await api.post(`/properties/${propId}/gbp/competitor`, { competitor_id: cid, query: q }); toast.success(L("Aggiornato", "Updated")); await load(true); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); }
    finally { setBusy(false); }
  };

  if (loading) return (
    <div className="bg-card border border-border rounded-lg p-6" data-testid="gv-loading">
      <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin" />{L("Analizzo la visibilità Google…", "Analyzing Google visibility…")}</div>
    </div>
  );
  if (!data) return null;

  const own = data.own || {};
  const audit = data.audit || {};
  const advice = data.advice || {};
  const mkt = data.market || {};
  const color = (audit.visibility_score ?? 0) >= 75 ? "var(--demand-low)" : (audit.visibility_score ?? 0) >= 50 ? "var(--demand-medium)" : "var(--demand-high)";

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="bg-card border border-border rounded-lg p-6" data-testid="google-visibility">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <div className="flex items-center gap-2"><MapPin className="w-5 h-5 text-primary" /><h2 className="text-lg font-semibold">{L("Visibilità & Profilo Google", "Google Visibility & Profile")}</h2></div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="ghost" onClick={() => setShowLink((s) => !s)} data-testid="gv-link-toggle"><Link2 className="w-4 h-4 mr-1" />{L("Collega/Correggi", "Link/Fix")}</Button>
          <Button size="sm" variant="outline" disabled={busy} onClick={() => load(true)} data-testid="gv-refresh"><RefreshCw className={`w-4 h-4 mr-1 ${busy ? "animate-spin" : ""}`} />{L("Aggiorna", "Refresh")}</Button>
        </div>
      </div>

      {showLink && (
        <div className="mb-4 flex items-center gap-2 flex-wrap rounded-lg bg-muted/40 p-3" data-testid="gv-link-panel">
          <input className="flex-1 min-w-[220px] h-9 rounded border border-border bg-background px-2 text-sm" placeholder={L("Link Google Maps o nome esatto della struttura", "Google Maps link or exact business name")} value={linkVal} onChange={(e) => setLinkVal(e.target.value)} data-testid="gv-link-input" />
          <Button size="sm" disabled={busy} onClick={saveLink} data-testid="gv-link-save">{L("Collega", "Link")}</Button>
        </div>
      )}

      {!data.connected ? (
        <div className="py-8 text-center text-sm text-muted-foreground" data-testid="gv-not-connected">
          {data.message || L("Scheda Google non trovata. Collega il profilo manualmente.", "Google listing not found. Link the profile manually.")}
        </div>
      ) : (
        <>
          {/* Own score + stats */}
          <div className="flex items-center gap-5 flex-wrap mb-5">
            <Ring score={audit.visibility_score ?? 0} color={color} />
            <div className="flex-1 min-w-[240px]">
              <div className="flex items-center gap-2 mb-2">
                <span className="font-semibold">{own.name}</span>
                {own.maps_uri && <a href={own.maps_uri} target="_blank" rel="noreferrer" className="text-primary" data-testid="gv-own-maps"><ExternalLink className="w-3.5 h-3.5" /></a>}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
                <Stat icon={Star} label={L("Valutazione", "Rating")} value={own.rating ?? "—"} sub={mkt.avg_rating ? L(`zona ${mkt.avg_rating}`, `area ${mkt.avg_rating}`) : ""} />
                <Stat icon={TrendingUp} label={L("Recensioni", "Reviews")} value={own.reviews ?? 0} sub={mkt.avg_reviews ? L(`zona ${Math.round(mkt.avg_reviews)}`, `area ${Math.round(mkt.avg_reviews)}`) : ""} />
                <Stat icon={ImageIcon} label={L("Foto", "Photos")} value={own.photos_count ?? 0} />
                <Stat icon={own.website ? Globe : Globe} label={L("Scheda", "Listing")} value={`${own.website ? "🌐" : "—"} ${own.phone ? "📞" : "—"} ${own.has_hours ? "🕒" : "—"}`} />
              </div>
            </div>
          </div>

          {/* Visibility trend over time */}
          {history.length >= 2 ? (
            <div className="rounded-lg border border-border p-4 mb-5" data-testid="gv-trend">
              <div className="flex items-center gap-2 mb-3"><LineChartIcon className="w-4 h-4 text-primary" /><span className="font-semibold text-sm">{L("Visibilità nel tempo", "Visibility over time")}</span></div>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={history} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: "var(--muted-foreground)" }} tickFormatter={(d) => (d || "").slice(5)} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: "var(--muted-foreground)" }} />
                  <Tooltip contentStyle={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 8, fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="visibility_score" name={L("La tua visibilità", "Your visibility")} stroke="#2563eb" strokeWidth={2.5} dot={{ r: 3, fill: "#2563eb" }} />
                  <Line type="monotone" dataKey="competitor_avg_score" name={L("Media competitor", "Competitor avg")} stroke="#f59e0b" strokeWidth={2} strokeDasharray="5 4" dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="rounded-lg border border-dashed border-border p-3 mb-5 text-xs text-muted-foreground flex items-center gap-2" data-testid="gv-trend-empty">
              <LineChartIcon className="w-4 h-4" />{L("Il grafico della visibilità nel tempo apparirà con più rilevazioni (una al giorno ad ogni aggiornamento).", "The visibility-over-time chart appears once there are more data points (one per day on refresh).")}
            </div>
          )}

          {/* AI advice */}
          <div className="rounded-lg border-2 border-primary/25 p-4 mb-5" data-testid="gv-advice">
            <div className="flex items-center gap-2 mb-1"><Lightbulb className="w-4 h-4 text-primary" /><span className="font-semibold text-sm">{advice.headline || L("Consigli per la visibilità", "Visibility advice")}</span></div>
            {advice.assessment && <p className="text-sm text-muted-foreground mb-3">{advice.assessment}</p>}
            <ul className="space-y-2">
              {(advice.recommendations || []).map((r, i) => (
                <li key={i} className="flex items-start gap-3 text-sm" data-testid={`gv-rec-${i}`}>
                  <span className="mt-1.5 w-2.5 h-2.5 rounded-full shrink-0" style={{ background: PRIO[r.priority] || PRIO.low }} />
                  <div>
                    <span className="font-medium">{r.action}</span>
                    {r.when && <span className="ml-2 text-[11px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-secondary text-muted-foreground"><Clock className="w-3 h-3 inline mr-0.5" />{r.when}</span>}
                    {r.why && <p className="text-xs text-muted-foreground mt-0.5">{r.why}</p>}
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Competitor comparison */}
          <div>
            <p className="text-xs uppercase tracking-wide font-bold text-muted-foreground mb-1 flex items-center gap-2">
              {L("Confronto competitor (stessa micro-zona)", "Competitor comparison (same micro-zone)")}
              <span className="inline-flex items-center gap-1 normal-case tracking-normal text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-secondary text-muted-foreground" data-testid="gv-comp-source"><Star className="w-3 h-3 text-amber-500" />{L("da Google", "from Google")}</span>
            </p>
            <p className="text-[11px] text-muted-foreground mb-3">{L("Solo strutture simili alla tua (gli hotel sono esclusi se la tua non è un hotel), ordinate per similarità.", "Only structures similar to yours (hotels excluded if yours is not a hotel), ranked by similarity.")}</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="gv-competitors">
              {(data.competitors || []).map((c) => (
                <div key={c.place_id} className="rounded-lg border border-border overflow-hidden" data-testid={`gv-comp-${c.place_id}`}>
                  <div className="h-24 bg-muted overflow-hidden">
                    {c.thumbnail ? <img src={photoUrl(c.thumbnail)} alt={c.name} className="w-full h-full object-cover" loading="lazy" /> : <div className="w-full h-full grid place-items-center text-muted-foreground"><ImageIcon className="w-6 h-6" /></div>}
                  </div>
                  <div className="p-2.5">
                    <div className="text-sm font-medium truncate" title={c.name}>{c.name}</div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                      <span className="inline-flex items-center gap-0.5"><Star className="w-3 h-3 text-amber-500" />{c.rating ?? "—"}</span>
                      <span>· {c.reviews ?? 0} rec.</span>
                      <span>· {c.photos_count ?? 0} <ImageIcon className="w-3 h-3 inline" /></span>
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-[11px] font-bold" style={{ color: (c.visibility_score ?? 0) >= 75 ? "var(--demand-low)" : (c.visibility_score ?? 0) >= 50 ? "var(--demand-medium)" : "var(--demand-high)" }}>Visibilità {Math.round(c.visibility_score ?? 0)}</span>
                      <button onClick={() => fixCompetitor(c.place_id)} className="text-[10px] text-muted-foreground hover:text-primary" data-testid={`gv-comp-fix-${c.place_id}`}>{L("correggi", "fix")}</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </motion.div>
  );
}

function Stat({ icon: Icon, label, value, sub }) {
  return (
    <div className="rounded-lg bg-muted/40 p-2">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground flex items-center gap-1"><Icon className="w-3 h-3" />{label}</div>
      <div className="font-data font-semibold">{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground">{sub}</div>}
    </div>
  );
}
